import { useState, useEffect, useCallback } from 'react';
import {
  Drawer,
  Button,
  Select,
  Input,
  message,
  Form,
  Spin,
  Row,
  Col,
  Empty,
  Tag,
  Pagination,
  Tooltip,
  Modal,
  Upload,
  Alert,
  Radio,
  Grid,
} from 'antd';
import { useTranslation } from 'react-i18next';
import {
  useClearOcppCache,
  useGetSingleCharger,
  useSetOcppConfig,
  useTriggerOcppConfig,
  useStartCharging,
  useGetDiagnostics,
  useStartDiagnostic,
  useChangeChargerAvailability,
  useSetLocalList,
  useRebootCharger,
  useGetStatusNotification,
  useUpdateFirmware,
  useGetFirmwareHistory,
  useUnlockConnector,
  useTriggerMessage,
} from '../Hooks';
import { useOperatorStore } from '../../../store/useOperatorStore';
import {
  DebouncedSelect,
  fetchCustomerList,
} from '../../../components/useDebouncedSelectLocation';
import { useFetchCustomerVehicles } from '../../ev-vehicle-models/Hooks';
import PropTypes from 'prop-types';
import {
  UserOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  ReloadOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { formatUnixToLocalDate } from '../../../utils/utils';
import { getErrorMessage } from '../../../utils/errorHandler';
import { useDownloadPresignedUrl } from '../../report/Hook/Index';
import { useFilUpload } from '../../../Hooks/Index';

const { useBreakpoint } = Grid;

const OcppCommandDrawer = ({ isOpen, onClose, charger, refetchLocation }) => {
  const { t } = useTranslation('ocppCommandDrawer');
  const [messageApi, contextHolder] = message.useMessage();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [command, setCommand] = useState('');
  const [connectorId, setConnectorId] = useState('');
  const [startChargingType, setStartChargingType] = useState('test');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [availabilityType, setAvailabilityType] = useState('');
  const [configUpdates, setConfigUpdates] = useState({});
  const [messageType, setMessageType] = useState('');
  const { operatorId } = useOperatorStore();
  const customerIdForVehicles =
    startChargingType === 'customer' && selectedCustomer?.value
      ? String(selectedCustomer.value)
      : null;
  const { data: customerVehiclesData, isLoading: vehiclesLoading } =
    useFetchCustomerVehicles(customerIdForVehicles);
  const [localList, setLocalList] = useState([
    { idTag: '', status: 'Accepted' },
  ]);
  const [localListErrors, setLocalListErrors] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalList, setModalList] = useState([]);
  const { mutateAsync: uploadFile } = useFilUpload();
  const { mutate: updateFirmware, isPending: isUpdatingFirmware } = useUpdateFirmware({
    onSuccess: () => {
      const firmwareKey = 'updatable-firmware';
      messageApi.open({
        key: firmwareKey,
        type: 'loading',
        content: t('loading.firmwareUpdateStarted'),
      });
      // Wait 5 seconds for firmware update to be processed
      setTimeout(() => {
        messageApi.open({
          key: firmwareKey,
          type: 'loading',
          content: t('loading.refreshingChargerData'),
        });
        refetchLocation();
        refetch();
        setTimeout(() => {
          messageApi.open({
            key: firmwareKey,
            type: 'success',
            content: t('success.firmwareUpdateStartedAndRefreshed'),
            duration: 2,
          });
        }, 500);
      }, 5000);
    },
    onError: (error) => {
      messageApi.error(error?.response?.data?.message || t('firmwareUpdateFailed', 'Failed to update firmware'));
    },
  });
  const [firmwareFile, setFirmwareFile] = useState(null);
  const [firmwareFileUrl, setFirmwareFileUrl] = useState('');
  const [isUploadingFirmware, setIsUploadingFirmware] = useState(false);
  const [firmwareSourceType, setFirmwareSourceType] = useState('upload'); // 'upload' or 'url'
  const [manualFirmwareUrl, setManualFirmwareUrl] = useState('');
  const [isWaitingForChargingRefresh, setIsWaitingForChargingRefresh] = useState(false);

  // Firmware history
  const [firmwareOffset, setFirmwareOffset] = useState(1);
  const [firmwarePageSize, setFirmwarePageSize] = useState(10);
  const {
    data: firmwareHistory,
    isLoading: isFirmwareHistoryLoading,
    isFetching: isFirmwareHistoryFetching,
    refetch: refetchFirmwareHistory,
  } = useGetFirmwareHistory(charger?._id, firmwareOffset, firmwarePageSize, {
    keepPreviousData: true,
    enabled: !!charger?._id && command === 'firmwareUpdate',
  });
  const firmwareList = firmwareHistory?.paginatedData || firmwareHistory?.data || [];
  const firmwareTotal = firmwareHistory?.totalCount || firmwareHistory?.total || 0;

  const {
    data: chargerData,
    isLoading,
    refetch,
  } = useGetSingleCharger({
    operatorId,
    id: charger?._id,
  });

  // Check if charger is charging
  const isChargerCharging = chargerData?.connectors?.some(
    (connector) => connector.status === 'Charging'
  );

  const { mutate: changeAvailability, isPending: isChangeAvailabilityLoading } =
    useChangeChargerAvailability();
  const { mutate: clearCache, isPending: isClearCacheLoading } =
    useClearOcppCache();
  const { mutate: setConfig, isPending: isSetConfigLoading } =
    useSetOcppConfig();
  const { mutate: triggerConfig, isPending: isTriggerConfigLoading } =
    useTriggerOcppConfig();
  const { mutate: startCharging, isPending: isStartChargingLoading } =
    useStartCharging({});
  const { mutate: startDiagnostic, isPending: isStartDiagnosticLoading } =
    useStartDiagnostic({
      onSuccess: () => {
        const diagnosticKey = 'updatable-diagnostic';
        messageApi.open({
          key: diagnosticKey,
          type: 'loading',
          content: t('loading.diagnosticStarted'),
        });
        // Wait 5 seconds for diagnostic to be generated
        setTimeout(() => {
          messageApi.open({
            key: diagnosticKey,
            type: 'loading',
            content: t('loading.refreshingDiagnosticData'),
          });
          refetchLocation();
          refetch();
          setTimeout(() => {
            messageApi.open({
              key: diagnosticKey,
              type: 'success',
              content: t('success.diagnosticStartedAndRefreshed'),
              duration: 2,
            });
          }, 500);
        }, 5000);
      },
      onError: (error) => {
        messageApi.error(error?.response?.data?.message || t('errors.startDiagnosticFailed'));
      },
    });
  const { mutate: downloadFile, isLoading: isDownloading } =
    useDownloadPresignedUrl({
      onSuccess: (data) => {
        const downloadUrl = data.data;
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', 'ArchivedLog.txt'); // You can use diag.fileName if available
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      },
      onError: (error) => {
        messageApi.error(getErrorMessage(error, t('errors.downloadReportFailed')));
      },
    });
  const { mutate: setLocalListMutate, isPending: isSetLocalListLoading } =
    useSetLocalList(charger?._id, {
      onSuccess: () => {
        const localListKey = 'updatable-local-list';
        messageApi.open({
          key: localListKey,
          type: 'loading',
          content: t('loading.localListSent'),
        });
        // Wait 5 seconds for charger to process the local list
        setTimeout(() => {
          messageApi.open({
            key: localListKey,
            type: 'loading',
            content: t('loading.refreshingChargerData'),
          });
          refetchLocation();
          refetch();
          setTimeout(() => {
            messageApi.open({
              key: localListKey,
              type: 'success',
              content: t('success.localListSentAndRefreshed'),
              duration: 2,
            });
          }, 500);
        }, 5000);
      },
      onError: (error) =>
        messageApi.error(
          error?.response?.data?.message || t('errors.sendLocalListFailed')
        ),
    });
  const { mutate: rebootCharger, isPending: isRebooting } = useRebootCharger({
    onSuccess: () => {
      const rebootKey = 'updatable-reboot';
      messageApi.open({
        key: rebootKey,
        type: 'loading',
        content: t('loading.rebootCommandSent'),
      });
      // Wait 10 seconds for charger to reboot (reboot takes longer)
      setTimeout(() => {
        messageApi.open({
          key: rebootKey,
          type: 'loading',
          content: t('loading.refreshingChargerData'),
        });
        refetchLocation();
        refetch();
        setTimeout(() => {
          messageApi.open({
            key: rebootKey,
            type: 'success',
            content: t('success.chargerRebootedAndRefreshed'),
            duration: 2,
          });
        }, 500);
      }, 10000);
    },
    onError: (error) => {
      messageApi.error(error?.response?.data?.message || t('errors.sendRebootCommandFailed'));
    },
  });
  const { mutate: getStatusNotification, isPending: isGettingStatus } = useGetStatusNotification({
    onSuccess: () => {
      messageApi.success(t('statusNotificationSent'));
    },
    onError: (error) => {
      messageApi.error(error?.response?.data?.message || t('errors.getStatusNotificationFailed'));
    },
  });

  const { mutate: unlockConnector, isPending: isUnlockingConnector } = useUnlockConnector({
    onSuccess: () => {
      const unlockKey = 'updatable-unlock';
      messageApi.open({
        key: unlockKey,
        type: 'loading',
        content: t('loading.unlockingConnector'),
      });
      // Wait 2 seconds for unlock to process
      setTimeout(() => {
        messageApi.open({
          key: unlockKey,
          type: 'loading',
          content: t('loading.refreshingChargerData'),
        });
        refetchLocation();
        refetch();
        setTimeout(() => {
          messageApi.open({
            key: unlockKey,
            type: 'success',
            content: t('success.connectorUnlockedAndRefreshed'),
            duration: 2,
          });
        }, 500);
      }, 2000);
    },
    onError: (error) => {
      messageApi.error(error?.response?.data?.message || t('errors.unlockConnectorFailed'));
    },
  });

  const { mutate: triggerMessage, isPending: isTriggeringMessage } = useTriggerMessage({
    onSuccess: () => {
      const triggerMessageKey = 'updatable-trigger-message';
      messageApi.open({
        key: triggerMessageKey,
        type: 'loading',
        content: t('loading.triggeringMessage'),
      });
      // Wait 2 seconds for message to be processed
      setTimeout(() => {
        messageApi.open({
          key: triggerMessageKey,
          type: 'loading',
          content: t('loading.refreshingChargerData'),
        });
        refetchLocation();
        refetch();
        setTimeout(() => {
          messageApi.open({
            key: triggerMessageKey,
            type: 'success',
            content: t('success.messageTriggeredAndRefreshed'),
            duration: 2,
          });
        }, 500);
      }, 2000);
    },
    onError: (error) => {
      messageApi.error(error?.response?.data?.message || t('errors.triggerMessageFailed'));
    },
  });

  const DiagnosticsList = ({ chargerId, type = 'Diagnostic' }) => {
    const [offset, setOffset] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const {
      data: diagnostics,
      isLoading,
      isFetching,
      refetch,
    } = useGetDiagnostics(chargerId, offset, pageSize, type, {
      keepPreviousData: true,
      enabled: !!chargerId,
    });
    const { t } = useTranslation('ocppCommandDrawer');
    const diagList = diagnostics?.paginatedData || diagnostics?.data || [];
    const total = diagnostics?.totalCount || diagnostics?.total || 0;

    const handlePageChange = (page, pageSize) => {
      setOffset(page);
      setPageSize(pageSize);
    };

    if (isLoading) return <Spin />;
    if (!diagList.length)
      return <Empty description={t('noDiagnosticsFound')} />;
    return (
      <div>
        <div className="flex items-center justify-between">
          <h4 className="mb-0 text-lg font-semibold">
            {type === 'LocalList'
              ? t('existingLocalLists', 'Existing Local Lists')
              : t('existingDiagnostics')}
          </h4>
          <Tooltip title={t('refresh')} placement="top">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetch()}
              loading={isFetching}
              htmlType="button"
              type="text"
            ></Button>
          </Tooltip>
        </div>
        <ul className="space-y-4 divide-y divide-gray-200 mt-2">
          {diagList.map((diag) => (
            <li key={diag._id} className="rounded-xl border border-gray-200 p-2 flex flex-col gap-2">
              <div className="flex items-center gap-2">
                {diag.fileUrl ? (
                  <Button
                    type="link"
                    className="!px-0"
                    icon={<FileTextOutlined className="text-base" />}
                    onClick={() => downloadFile({ fileUrl: diag.fileUrl.replace('https://', 's3://') })}
                    disabled={isDownloading}
                    htmlType="button"
                  >
                    {isDownloading
                      ? t('downloading')
                      : diag.fileName || t('download')}
                  </Button>
                ) : (
                  <span className="text-gray-400 font-semibold text-base">
                    {diag.fileName || t('download')} ({t('notAvailable')})
                  </span>
                )}
              </div>
              <div>
                <Tag
                  variant="outlined"
                  color={
                    diag.status === 'Uploaded'
                      ? 'green'
                      : diag.status === 'Idle'
                        ? 'orange'
                        : 'blue'
                  }
                  className="ml-auto"
                  style={{ fontWeight: 500 }}
                >
                  {diag.status}
                </Tag>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-gray-600 text-xs">
                <span className="flex items-center">
                  <ClockCircleOutlined className="mr-1" />
                  <span className="mr-1">{t('createdAt')}:</span>
                  {diag.created_at
                    ? formatUnixToLocalDate(diag.created_at)
                    : '-'}
                </span>

                <span className="flex items-center">
                  <UserOutlined className="mr-1" />
                  <span className="mr-1">{t('performedBy')}:</span>{' '}
                  {diag.performerName || '-'}
                </span>
              </div>
            </li>
          ))}
        </ul>
        <div className="flex justify-center mt-4">
          <Pagination
            current={Math.floor(offset / pageSize) + 1}
            pageSize={pageSize}
            total={total}
            onChange={handlePageChange}
            showSizeChanger={false}
          />
        </div>
      </div>
    );
  };

  const LocalListHistory = ({ chargerId }) => {
    const [offset, setOffset] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const {
      data: localListData,
      isLoading,
      isFetching,
      refetch,
    } = useGetDiagnostics(chargerId, offset, pageSize, 'LocalList', {
      keepPreviousData: true,
      enabled: !!chargerId,
    });
    const { t } = useTranslation('ocppCommandDrawer');
    const list = localListData?.paginatedData || localListData?.data || [];
    const total = localListData?.totalCount || localListData?.total || 0;

    const handlePageChange = (page, pageSize) => {
      setOffset(page);
      setPageSize(pageSize);
    };

    if (isLoading) return <Spin />;
    if (!list.length) return <Empty description={t('noLocalListsFound', 'No Local Lists Found')} />;
    return (
      <div>
        <div className="flex items-center justify-between">
          <h4 className="mb-0 text-lg font-semibold">
            {t('historyLocalList')}
          </h4>
          <Tooltip title={t('refresh')} placement="top">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetch()}
              isLoading={isFetching}
              htmlType="button"
              type="text"
            ></Button>
          </Tooltip>
        </div>
        <ul className="space-y-4 mt-2">
          {list.map((item) => (
            <li
              key={item._id}
              className="bg-white rounded-lg border space-y-2 border-gray-100 p-5 mb-4 list-none"
            >
              <div className="flex items-center">
                <div className="flex-1 flex flex-wrap gap-4">
                  <span className="font-semibold">
                    {t('createdAt', 'Created At')}: <span className="font-normal">{item.created_at ? formatUnixToLocalDate(item.created_at) : '-'}</span>
                  </span>
                  <span className="font-semibold">
                    {t('performedBy', 'Performed By')}: <span className="font-normal">{item.performerName || '-'}</span>
                  </span>
                </div>
                <Tag
                  variant="outlined"
                  color={item.status === 'Uploaded' ? 'green' : item.status === 'Uploading' ? 'orange' : 'blue'}
                  className="font-semibold ml-auto text-center"
                >
                  {item.status}
                </Tag>
              </div>
              
              <div className="text-sm mt-2">
                <b>{t('description', 'Description')}:</b> {item.description || '-'}
              </div>
              {Array.isArray(item.localAuthorizationList) && item.localAuthorizationList.length > 0 ? (
                <div className="flex justify-start mt-2">
                  <Button
                    size="small"
                    type="text"
                    ghost
                    onClick={() => {
                      setModalList(item.localAuthorizationList);
                      setModalVisible(true);
                    }}
                    className="rounded-md font-medium"
                  >
                    {t('viewLocalAuthorizationList', 'View Local Authorization List')}
                  </Button>
                </div>
              ) : (
                <div className="text-gray-400 text-center">{t('noIdTags', 'No idTags in this version')}</div>
              )}
            </li>
          ))}
        </ul>
        <div className="flex justify-center mt-4">
          <Pagination
            current={Math.floor(offset / pageSize) + 1}
            pageSize={pageSize}
            total={total}
            onChange={handlePageChange}
            showSizeChanger={false}
          />
        </div>
        <Modal
          title={t('localAuthorizationList', 'Local Authorization List')}
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={null}
        >
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {modalList && modalList.length > 0 ? (
              modalList.map((auth, idx) => (
                <li key={idx} style={{ marginBottom: 8 }}>
                  <span>
                    <b>{t('idTag', 'idTag')}:</b> {auth.idTag || '-'}
                  </span>
                  {auth.idTagInfo && (
                    <span style={{ marginLeft: 8 }}>
                      <b>{t('status', 'Status')}:</b> {auth.idTagInfo.status || '-'}
                    </span>
                  )}
                </li>
              ))
            ) : (
              <div style={{ color: '#888' }}>{t('noIdTags', 'No idTags in this version')}</div>
            )}
          </ul>
        </Modal>
      </div>
    );
  };

  useEffect(() => {
    if (command === 'setConfig' && !chargerData?.configurations?.length) {
      const autoTriggerKey = 'updatable-auto-trigger-config';
      messageApi.open({
        key: autoTriggerKey,
        type: 'loading',
        content: t('loading.loadingConfigurations'),
      });
      triggerConfig(
        { operatorId: charger.operatorId, serialNumber: charger.serialNumber },
        {
          onSuccess: () => {
            messageApi.open({
              key: autoTriggerKey,
              type: 'loading',
              content: t('loading.configurationsLoaded'),
            });
            setTimeout(() => {
              messageApi.open({
                key: autoTriggerKey,
                type: 'loading',
                content: t('loading.refreshingConfigurationData'),
              });
              refetch();
              setTimeout(() => {
                messageApi.open({
                  key: autoTriggerKey,
                  type: 'success',
                  content: t('success.configTriggeredAndRefreshed'),
                  duration: 2,
                });
              }, 500);
            }, 5000);
          },
          onError: (error) => {
            messageApi.open({
              key: autoTriggerKey,
              type: 'error',
              content: getErrorMessage(error, t('messages.configTriggerFailed')),
              duration: 2,
            });
          },
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [command]);

  useEffect(() => {
    if (command === 'setLocalList') {
      if (Array.isArray(chargerData?.localAuthorizationList) && chargerData.localAuthorizationList.length > 0) {
        setLocalList(
          chargerData.localAuthorizationList.map((auth) => ({
            idTag: auth.idTag || '',
            status: auth.idTagInfo?.status || 'Accepted',
          }))
        );
      } else {
        setLocalList([{ idTag: '', status: 'Accepted' }]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [command]);

  // Reset waiting state when command changes or drawer closes
  useEffect(() => {
    if (!isOpen || command !== 'startCharging') {
      setIsWaitingForChargingRefresh(false);
    }
  }, [isOpen, command]);

  // Reset start charging specific state when command changes
  useEffect(() => {
    if (command !== 'startCharging') {
      setStartChargingType('test');
      setSelectedCustomer(null);
    }
  }, [command]);

  // Reset messageType when command changes
  useEffect(() => {
    if (command !== 'triggerMessage') {
      setMessageType('');
    }
  }, [command]);

  const handleConfigChange = (key, value) => {
    setConfigUpdates((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleLocalListChange = (index, field, value) => {
    setLocalList((prev) => {
      const updated = [...prev];
      updated[index][field] = value;
      return updated;
    });
  };

  const handleAddLocalListEntry = () => {
    setLocalList((prev) => {
      if (prev.length >= 10) return prev;
      return [...prev, { idTag: '', status: 'Accepted' }];
    });
  };

  const handleRemoveLocalListEntry = (index) => {
    setLocalList((prev) => prev.filter((_, i) => i !== index));
  };

  const validateLocalList = useCallback((list) => {
    const errors = list.map((item, idx, arr) => {
      let error = '';
      if (!item.idTag) {
        error = t('errors.idTagRequired');
      } else if (!/^[a-zA-Z0-9]+$/.test(item.idTag)) {
        error = t('errors.idTagAlphanumeric');
      } else if (arr.findIndex((x) => x.idTag === item.idTag) !== idx) {
        error = t('errors.idTagUnique');
      }
      return error;
    });
    return errors;
  }, [t]);

  useEffect(() => {
    setLocalListErrors(validateLocalList(localList));
  }, [localList, validateLocalList]);

  const handleExecuteCommand = () => {
    if (isButtonDisabled) {
      if (command === 'setLocalList' && localListErrors.some((err) => err)) {
        messageApi.error(t('errors.fixLocalListErrors'));
      }
      return;
    }
    const data = {
      operatorId: charger.operatorId,
      serialNumber: charger.serialNumber,
    };
    let keyValues;
    switch (command) {
      case 'changeAvailability': {
        const availabilityKey = 'updatable-availability';
        messageApi.open({
          key: availabilityKey,
          type: 'loading',
          content: t('loading.changingAvailability'),
        });
        changeAvailability(
          { ...data, type: availabilityType, connectorId },
          {
            onSuccess: () => {
              messageApi.open({
                key: availabilityKey,
                type: 'loading',
                content: t('loading.availabilityChanged'),
              });
              setTimeout(() => {
                refetchLocation();
                refetch();
                messageApi.open({
                  key: availabilityKey,
                  type: 'success',
                  content: t('success.availabilityChangedAndRefreshed'),
                  duration: 2,
                });
              }, 2000);
            },
            onError: (error) => {
              messageApi.open({
                key: availabilityKey,
                type: 'error',
                content: error?.response?.data?.message || t('messages.availabilityChangeFailed'),
                duration: 2,
              });
            },
          }
        );
        break;
      }
      case 'clearCache': {
        const cacheKey = 'updatable-cache';
        messageApi.open({
          key: cacheKey,
          type: 'loading',
          content: t('loading.clearingCache'),
        });
        clearCache(data, {
          onSuccess: () => {
            messageApi.open({
              key: cacheKey,
              type: 'loading',
              content: t('loading.cacheCleared'),
            });
            setTimeout(() => {
              refetchLocation();
              refetch();
              messageApi.open({
                key: cacheKey,
                type: 'success',
                content: t('success.cacheClearedAndRefreshed'),
                duration: 2,
              });
            }, 2000);
          },
          onError: (error) => {
            messageApi.open({
              key: cacheKey,
              type: 'error',
              content: error?.response?.data?.message || t('messages.cacheClearFailed'),
              duration: 2,
            });
          },
        });
        break;
      }
      case 'setConfig': {
        if (Object.keys(configUpdates).length === 0) {
          messageApi.error(t('messages.modifyConfig'));
          return;
        }
        keyValues = Object.entries(configUpdates).map(([key, value]) => ({
          key,
          value,
        }));
        const configKey = 'updatable-config';
        // Show loading message immediately when update starts
        messageApi.open({
          key: configKey,
          type: 'loading',
          content: t('loading.updatingConfigurations'),
        });
        setConfig(
          { ...data, keyValues },
          {
            onSuccess: () => {
              // Update message to show we're triggering get default config
              messageApi.open({
                key: configKey,
                type: 'loading',
                content: t('loading.configurationsUpdated'),
              });
              // Trigger get default config after successful update
              triggerConfig(data, {
                onSuccess: () => {
                  messageApi.open({
                    key: configKey,
                    type: 'loading',
                    content: t('loading.defaultConfigTriggered'),
                  });
                  // Wait 5 seconds for charger to process the configuration
                  setTimeout(() => {
                    messageApi.open({
                      key: configKey,
                      type: 'loading',
                      content: t('loading.refreshingChargerData'),
                    });
                    refetchLocation();
                    refetch();
                    // Show success message after refresh
                    setTimeout(() => {
                      messageApi.open({
                        key: configKey,
                        type: 'success',
                        content: t('success.configurationsUpdatedAndRefreshed'),
                        duration: 2,
                      });
                    }, 500);
                  }, 5000);
                },
                onError: (error) => {
                  messageApi.open({
                    key: configKey,
                    type: 'error',
                    content: error?.response?.data?.message || t('messages.configTriggerFailed'),
                    duration: 2,
                  });
                },
              });
            },
            onError: (error) => {
              messageApi.open({
                key: configKey,
                type: 'error',
                content: error?.response?.data?.message || t('messages.configUpdateFailed'),
                duration: 2,
              });
            },
          }
        );
        break;
      }
      case 'triggerConfig': {
        const triggerKey = 'updatable-trigger-config';
        messageApi.open({
          key: triggerKey,
          type: 'loading',
          content: t('loading.triggeringGetDefaultConfig'),
        });
        triggerConfig(data, {
          onSuccess: () => {
            messageApi.open({
              key: triggerKey,
              type: 'loading',
              content: t('loading.defaultConfigTriggered'),
            });
            // Wait 5 seconds for charger to process
            setTimeout(() => {
              messageApi.open({
                key: triggerKey,
                type: 'loading',
                content: t('loading.refreshingChargerData'),
              });
              refetchLocation();
              refetch();
              setTimeout(() => {
                messageApi.open({
                  key: triggerKey,
                  type: 'success',
                  content: t('success.defaultConfigRetrievedAndRefreshed'),
                  duration: 2,
                });
              }, 500);
            }, 5000);
          },
          onError: (error) => {
            messageApi.open({
              key: triggerKey,
              type: 'error',
              content: error?.response?.data?.message || t('messages.configTriggerFailed'),
              duration: 2,
            });
          },
        });
        break;
      }
      case 'startCharging': {
        const chargingKey = 'updatable-charging';
        messageApi.open({
          key: chargingKey,
          type: 'loading',
          content: t('loading.startingCharging'),
        });
        startCharging(
          {
            chargerId: charger._id,
            connectorId,
            operatorId: charger.operatorId,
            ...(startChargingType === 'customer' && selectedCustomer?.value
              ? { customerId: String(selectedCustomer.value) }
              : {}),
            ...(startChargingType === 'customer' && selectedVehicleId
              ? { vehicleId: String(selectedVehicleId) }
              : {}),
          },
          {
            onSuccess: () => {
              setIsWaitingForChargingRefresh(true);
              messageApi.open({
                key: chargingKey,
                type: 'loading',
                content: t('loading.chargingStarted'),
              });
              // Wait 10 seconds for charging to start
              setTimeout(() => {
                messageApi.open({
                  key: chargingKey,
                  type: 'loading',
                  content: t('loading.refreshingChargerData'),
                });
                refetchLocation();
                refetch();
                setTimeout(() => {
                  setIsWaitingForChargingRefresh(false);
                  messageApi.open({
                    key: chargingKey,
                    type: 'success',
                    content: t('success.chargingStartedAndRefreshed'),
                    duration: 2,
                  });
                }, 500);
              }, 10000);
            },
            onError: (error) => {
              setIsWaitingForChargingRefresh(false);
              messageApi.open({
                key: chargingKey,
                type: 'error',
                content: error?.response?.data?.message || t('messages.chargingStartFailed'),
                duration: 2,
              });
            },
          }
        );
        break;
      }
      case 'startDiagnostic':
        startDiagnostic(charger._id);
        break;
      case 'setLocalList':
        if (!localList.length || localList.some((item) => !item.idTag)) {
          messageApi.error(t('errors.provideValidIdTag'));
          return;
        }
        setLocalListMutate({
          operatorId: charger.operatorId,
          localList: localList.map((item) => ({
            idTag: item.idTag,
            idTagInfo: { status: item.status },
          })),
        });
        break;
      case 'rebootCharger':
        rebootCharger({
          chargerId: charger._id,
          operatorId: charger.operatorId,
        });
        break;
      case 'getStatusNotification':
        getStatusNotification(charger._id);
        break;
      case 'unlockConnector':
        unlockConnector({
          chargerId: charger._id,
          connectorId,
        });
        break;
      case 'firmwareUpdate':
        handleFirmwareUpdate();
        break;
      case 'triggerMessage':
        if (!messageType) {
          messageApi.error(t('errors.selectMessageType'));
          return;
        }
        triggerMessage({
          chargerId: charger._id,
          type: messageType,
        });
        break;
      default:
        messageApi.error(t('messages.invalidCommand'));
    }
  };

  // Disable button logic
  const isButtonDisabled =
    !command ||
    (command === 'changeAvailability' && (!availabilityType || !connectorId)) ||
    (command === 'startCharging' && (
      !connectorId ||
      !startChargingType ||
      (startChargingType === 'customer' && !selectedCustomer?.value)
    )) ||
    (command === 'unlockConnector' && !connectorId) ||
    (command === 'setConfig' && Object.keys(configUpdates).length === 0) ||
    (command === 'triggerMessage' && !messageType) ||
    (command === 'setLocalList' && (
      !localList.length ||
      localList.some((item) => !item.idTag) ||
      localListErrors.some((err) => err)
    ));

  const handleFirmwareUpdate = () => {
    if (!firmwareFileUrl) {
      messageApi.error(t('selectFirmwareFile', 'Please upload a firmware file first'));
      return;
    }
    updateFirmware({
      chargerId: charger._id,
      operatorId: charger.operatorId,
      firmwareFilePath: firmwareFileUrl?.Location?.replace('https://', 'http://'),
    });
  };

  return (
    <>
      {contextHolder}
      <Drawer
        title={t('title')}
        placement="right"
        onClose={onClose}
        open={isOpen}
        width={isMobile ? '100%' : 700}
      >
        {isLoading ? (
          <Spin className="flex justify-center items-center w-full" />
        ) : (
          <Form layout="vertical">
          <Form.Item label={t('selectCommand')} required>
            <Select
              placeholder={t('selectCommand')}
              onChange={setCommand}
              className="w-full"
              value={command}
            >
              <Select.Option value="changeAvailability">
                {t('changeAvailability')}
              </Select.Option>
              <Select.Option value="clearCache">
                {t('clearCache')}
              </Select.Option>
              <Select.Option value="setConfig" disabled={isChargerCharging}>
                {t('setConfig')}
                {isChargerCharging && (
                  <span className="text-red-500 ml-2">
                    ({t('disabledWhileCharging')})
                  </span>
                )}
              </Select.Option>
             
              <Select.Option value="triggerMessage">
                {t('triggerMessage')}
              </Select.Option>
              <Select.Option value="startCharging">
                {t('startCharging')}
              </Select.Option>
              <Select.Option value="startDiagnostic">
                {t('startDiagnostic')}
              </Select.Option>
              <Select.Option value="setLocalList">
                {t('setLocalList')}
              </Select.Option>
              <Select.Option value="rebootCharger">
                {t('rebootCharger')}
              </Select.Option>
              <Select.Option value="getStatusNotification">
                {t('getStatusNotification')}
              </Select.Option>
              <Select.Option value="unlockConnector">
                {t('unlockConnector')}
              </Select.Option>
              <Select.Option value="firmwareUpdate">
                {t('firmwareUpdate')}
              </Select.Option>
            </Select>
          </Form.Item>

          {command && t(`commandDescriptions.${command}`) && (
            <Form.Item>
              <Alert
                type="info"
                showIcon
                message={
                  <div className="text-sm">
                    <div>{t(`commandDescriptions.${command}`)}</div>
                  </div>
                }
                style={{ backgroundColor: '#ffffff', borderColor: '#d9d9d9' }}
              />
            </Form.Item>
          )}

          {command === 'changeAvailability' && (
            <>
              <Form.Item label={t('selectAvailabilityType')} required>
                <Select
                  placeholder={t('selectAvailabilityType')}
                  onChange={setAvailabilityType}
                  className="w-full"
                  value={availabilityType}
                >
                  <Select.Option value="Operative">
                    {t('operative')}
                  </Select.Option>
                  <Select.Option value="Inoperative">
                    {t('inoperative')}
                  </Select.Option>
                </Select>
              </Form.Item>
              <Form.Item label={t('selectConnector')} required>
                <Select
                  placeholder={t('selectConnector')}
                  onChange={setConnectorId}
                  className="w-full"
                  value={connectorId}
                >
                  {chargerData?.connectors?.map((connector) => (
                    <Select.Option
                      key={connector.connectorId}
                      value={connector.connectorId}
                    >
                      {t(
                        `Connector ${connector.connectorId} - ${connector.connectorType}`
                      )}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </>
          )}

          {command === 'setConfig' && chargerData?.configurations?.length ? (
            <>
              <Form.Item>
                <Button
                  type="default"
                  onClick={() => {
                    const buttonTriggerKey = 'updatable-button-trigger-config';
                    messageApi.open({
                      key: buttonTriggerKey,
                      type: 'loading',
                      content: t('loading.triggeringGetDefaultConfig'),
                    });
                    triggerConfig(
                      {
                        operatorId: charger.operatorId,
                        serialNumber: charger.serialNumber,
                      },
                      {
                        onSuccess: () => {
                          messageApi.open({
                            key: buttonTriggerKey,
                            type: 'loading',
                            content: t('loading.defaultConfigTriggered'),
                          });
                          setTimeout(() => {
                            messageApi.open({
                              key: buttonTriggerKey,
                              type: 'loading',
                              content: t('loading.refreshingConfigurationData'),
                            });
                            refetch();
                            setTimeout(() => {
                              messageApi.open({
                                key: buttonTriggerKey,
                                type: 'success',
                                content: t('success.configTriggeredAndRefreshed'),
                                duration: 2,
                              });
                            }, 500);
                          }, 5000);
                        },
                        onError: (error) => {
                          messageApi.open({
                            key: buttonTriggerKey,
                            type: 'error',
                            content: getErrorMessage(error, t('messages.configTriggerFailed')),
                            duration: 2,
                          });
                        },
                      }
                    );
                  }}
                  loading={isTriggerConfigLoading}
                >
                  {t('getDefaultConfig')}
                </Button>
              </Form.Item>
              <Row gutter={[16, 16]}>
                {chargerData.configurations
                  .sort((a, b) => a.readonly - b.readonly)
                  .map((config) => (
                    <Col key={config.key} xs={24} sm={12}>
                      <Form.Item label={config.key} required={!config.readonly}>
                        <Input
                          placeholder={t('configValue')}
                          defaultValue={config.value}
                          onChange={(e) =>
                            handleConfigChange(config.key, e.target.value)
                          }
                          className="w-full"
                          disabled={config.readonly}
                        />
                      </Form.Item>
                    </Col>
                  ))}
              </Row>
            </>
          ) : null}
          {/* Extracted nested ternary operation into an independent statement */}
          {command === 'setConfig' && !chargerData?.configurations?.length && (
            <Spin className="flex justify-center items-center w-full" />
          )}

          {command === 'startCharging' && (
            <>
              <Form.Item
                label={t('selectConnector')}
                required
                tooltip={t('connectorMustBePreparingOrAvailable')}
              >
                <Select
                  placeholder={t('selectConnector')}
                  onChange={(value) => {
                    setConnectorId(value);
                    setStartChargingType('test');
                    setSelectedCustomer(null);
                    setSelectedVehicleId(null);
                  }}
                  className="w-full"
                  value={connectorId}
                  notFoundContent={
                    <Empty description={t('noConnectorsAvailable')} />
                  }
                >
                  {chargerData?.connectors
                    ?.filter(
                      (connector) =>
                        connector.status === 'Available' ||
                        connector.status === 'Preparing'
                    )
                    .map((connector) => (
                      <Select.Option key={connector._id} value={connector._id}>
                        {t(
                          `Connector ${connector.connectorId} - ${connector.connectorType} - ${connector.status}`
                        )}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>

              {connectorId && (
                <Form.Item label={t('chargingType', 'Charging Type')} required>
                  <Radio.Group
                    value={startChargingType}
                    onChange={(e) => {
                      setStartChargingType(e.target.value);
                      if (e.target.value !== 'customer') {
                        setSelectedCustomer(null);
                        setSelectedVehicleId(null);
                      }
                    }}
                  >
                    <Radio value="test">
                      {t('testCharging', 'Test Charging')}
                    </Radio>
                    <Radio value="customer">
                      {t('usingCustomer', 'Using Employee')}
                    </Radio>
                  </Radio.Group>
                </Form.Item>
              )}

              {connectorId && startChargingType === 'customer' && (
                <Form.Item
                  label={t('selectCustomer', 'Select Employee')}
                  required
                >
                  <DebouncedSelect
                    showSearch
                    allowClear
                    labelInValue
                    loadOnOpen
                    placeholder={t('searchCustomer', 'Search employee')}
                    fetchOptions={(search) =>
                      fetchCustomerList({ operatorId: charger?.operatorId, search })
                    }
                    value={selectedCustomer}
                    onChange={(value) => {
                      setSelectedCustomer(value);
                      setSelectedVehicleId(null);
                    }}
                  />
                </Form.Item>
              )}

              {connectorId &&
                startChargingType === 'customer' &&
                selectedCustomer?.value && (
                  <Form.Item
                    label={t('selectVehicle', 'Vehicle (optional)')}
                  >
                    <Select
                      allowClear
                      loading={vehiclesLoading}
                      placeholder={t(
                        'selectVehiclePlaceholder',
                        'Select vehicle or leave empty'
                      )}
                      value={selectedVehicleId}
                      onChange={(value) => setSelectedVehicleId(value || null)}
                      options={(customerVehiclesData?.paginatedData || []).map(
                        (vehicle) => ({
                          value: vehicle._id,
                          label: [vehicle.name, vehicle.vehicleNumber]
                            .filter(Boolean)
                            .join(' — '),
                        })
                      )}
                      notFoundContent={
                        <Empty
                          description={t(
                            'noVehicles',
                            'No vehicles for this employee'
                          )}
                        />
                      }
                    />
                  </Form.Item>
                )}
            </>
          )}

          {command === 'startCharging' && isWaitingForChargingRefresh && (
            <Form.Item>
              <div className="flex items-center justify-center gap-2 py-4">
                <Spin size="small" />
                <span className="text-sm text-gray-600">
                  {t('loading.refreshingChargerData')}
                </span>
              </div>
            </Form.Item>
          )}

          {command === 'unlockConnector' && (
            <Form.Item
              label={t('selectConnector')}
              required
              tooltip={t('selectConnectorToUnlock')}
            >
              <Select
                placeholder={t('selectConnector')}
                onChange={setConnectorId}
                className="w-full"
                value={connectorId}
                notFoundContent={
                  <Empty description={t('noConnectorsAvailable')} />
                }
              >
                {chargerData?.connectors?.map((connector) => (
                  <Select.Option key={connector._id} value={connector._id}>
                    {t(
                      `Connector ${connector.connectorId} - ${connector.connectorType} - ${connector.status}`
                    )}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {command === 'triggerMessage' && (
            <Form.Item
              label={t('selectMessageType')}
              required
              tooltip={t('selectMessageTypeTooltip')}
            >
              <Select
                placeholder={t('selectMessageType')}
                onChange={setMessageType}
                className="w-full"
                value={messageType}
              >
                <Select.Option value="BootNotification">
                  BootNotification
                </Select.Option>
                <Select.Option value="DiagnosticsStatusNotification">
                  DiagnosticsStatusNotification
                </Select.Option>
                <Select.Option value="FirmwareStatusNotification">
                  FirmwareStatusNotification
                </Select.Option>
                <Select.Option value="Heartbeat">
                  Heartbeat
                </Select.Option>
                <Select.Option value="MeterValues">
                  MeterValues
                </Select.Option>
                <Select.Option value="StatusNotification">
                  StatusNotification
                </Select.Option>
              </Select>
            </Form.Item>
          )}

          {command === 'startDiagnostic' && (
            <>
              <Form.Item>
                <Button
                  type="primary"
                  loading={isStartDiagnosticLoading}
                  onClick={() => handleExecuteCommand()}
                  disabled={isStartDiagnosticLoading}
                >
                  {t('startDiagnostic')}
                </Button>
              </Form.Item>
              {(isStartDiagnosticLoading || (Array.isArray(chargerData?.diagnostics) && chargerData.diagnostics.length === 0)) && (
                <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Spin size="small" />
                  <span>{t('diagnosticMayTakeTime')}</span>
                </div>
              )}
              <DiagnosticsList chargerId={charger._id} type="Diagnostic" />
            </>
          )}

          {command === 'setLocalList' && (
            <>
              <Form.Item label={t('localList', 'Local List')} required>
                {localList.map((item, idx) => (
                  <div key={idx} style={{ marginBottom: 8 }}>
                    <Row gutter={8} align="top">
                      <Col span={10}>
                        <Form.Item
                          validateStatus={localListErrors[idx] ? 'error' : ''}
                          help={localListErrors[idx] || ''}
                          style={{ marginBottom: 0 }}
                        >
                          <Input
                            placeholder={t('idTag', 'idTag')}
                            value={item.idTag}
                            onChange={(e) => handleLocalListChange(idx, 'idTag', e.target.value)}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={10}>
                        <Form.Item 
                          style={{ marginBottom: localListErrors[idx] ? 22 : 0 }}
                        >
                          <Select
                            value={item.status}
                            onChange={(value) => handleLocalListChange(idx, 'status', value)}
                            style={{ width: '100%' }}
                          >
                            <Select.Option value="Accepted">Accepted</Select.Option>
                            <Select.Option value="Blocked">Blocked</Select.Option>
                            <Select.Option value="Expired">Expired</Select.Option>
                            <Select.Option value="Invalid">Invalid</Select.Option>
                            <Select.Option value="ConcurrentTx">ConcurrentTx</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item 
                          style={{ marginBottom: localListErrors[idx] ? 22 : 0 }}
                        >
                          <Tooltip title={t('remove', 'Remove')}>
                            <Button 
                              danger 
                              icon={<DeleteOutlined />} 
                              onClick={() => handleRemoveLocalListEntry(idx)}
                              style={{ width: '100%' }}
                            />
                          </Tooltip>
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                ))}
                <Button
                  type="dashed"
                  onClick={handleAddLocalListEntry}
                  style={{ width: '100%' }}
                  disabled={localList.length >= 10}
                >
                  {t('addIdTag', 'Add idTag')}
                </Button>
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  onClick={handleExecuteCommand}
                  disabled={isButtonDisabled}
                  loading={isSetLocalListLoading}
                >
                  {t('executeCommand')}
                </Button>
              </Form.Item>
              <LocalListHistory chargerId={charger._id} />
            </>
          )}

          {command === 'firmwareUpdate' && (
            <>
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message={<span className="text-sm">{t('firmwareUpdateNote', 'Make sure there is no charging session in progress. If a charging session is ongoing, please stop it before updating firmware.')}</span>}
              />
              <Form.Item label={<span className="text-sm">{t('firmwareSourceType', 'Firmware Source')}</span>} required>
                <Radio.Group
                  value={firmwareSourceType}
                  onChange={e => {
                    setFirmwareSourceType(e.target.value);
                    // Reset values when switching
                    if (e.target.value === 'upload') setManualFirmwareUrl('');
                    if (e.target.value === 'url') {
                      setFirmwareFile(null);
                      setFirmwareFileUrl('');
                    }
                  }}
                  className="text-sm"
                >
                  <Radio value="upload" className="text-sm">{t('uploadFile', 'Upload File')}</Radio>
                  <Radio value="url" className="text-sm">{t('enterUrl', 'Enter URL')}</Radio>
                </Radio.Group>
              </Form.Item>
              {firmwareSourceType === 'upload' && (
                <Form.Item label={<span className="text-sm">{t('firmwareFile', 'Firmware File')}</span>} required>
                  <Upload
                    accept=".bin,.zip,.tar,.gz"
                    customRequest={async ({ file, onSuccess, onError }) => {
                      setIsUploadingFirmware(true);
                      try {
                        const url = await uploadFile(file);
                        setFirmwareFile(file);
                        setFirmwareFileUrl(url);
                        messageApi.success(t('firmwareFileUploaded', 'Firmware file uploaded successfully'));
                        if (onSuccess) onSuccess({});
                      } catch {
                        messageApi.error(t('firmwareFileUploadFailed', 'Failed to upload firmware file'));
                        setFirmwareFile(null);
                        setFirmwareFileUrl('');
                        if (onError) onError();
                      } finally {
                        setIsUploadingFirmware(false);
                      }
                    }}
                    showUploadList={{ showRemoveIcon: true }}
                    fileList={firmwareFile ? [{
                      uid: '-1',
                      name: firmwareFile.name,
                      status: 'done',
                      url: firmwareFileUrl,
                    }] : []}
                    onRemove={() => {
                      setFirmwareFile(null);
                      setFirmwareFileUrl('');
                    }}
                    disabled={isUploadingFirmware || isUpdatingFirmware}
                    maxCount={1}
                  >
                    <Button disabled={isUploadingFirmware || isUpdatingFirmware} className="text-sm">
                      {t('selectFirmwareFile', 'Select Firmware File')}
                    </Button>
                  </Upload>
                </Form.Item>
              )}
              {firmwareSourceType === 'url' && (
                <Form.Item label={<span className="text-sm">{t('firmwareFileUrl', 'Firmware File URL')}</span>} required>
                  <Input
                    value={manualFirmwareUrl}
                    onChange={e => setManualFirmwareUrl(e.target.value)}
                    placeholder={t('enterFirmwareUrl', 'Enter firmware file URL')}
                    disabled={isUploadingFirmware || isUpdatingFirmware}
                    className="text-sm"
                  />
                </Form.Item>
              )}
              <Form.Item>
                <Tooltip
                  title={isChargerCharging ? t('firmwareUpdateDisabledCharging', 'Firmware update is disabled while a charging session is in progress.') : ''}
                >
                  <Button
                    type="primary"
                    onClick={() => {
                      if (firmwareSourceType === 'upload') {
                        handleFirmwareUpdate();
                      } else if (firmwareSourceType === 'url') {
                        if (!manualFirmwareUrl || !/^https?:\/\//.test(manualFirmwareUrl)) {
                          messageApi.error(t('invalidFirmwareUrl', 'Please enter a valid firmware file URL starting with http:// or https://.'));
                          return;
                        }
                        updateFirmware({
                          chargerId: charger._id,
                          operatorId: charger.operatorId,
                          firmwareFilePath: manualFirmwareUrl,
                        });
                      }
                    }}
                    loading={isUploadingFirmware || isUpdatingFirmware}
                    disabled={
                      isUploadingFirmware ||
                      isUpdatingFirmware ||
                      isChargerCharging ||
                      (firmwareSourceType === 'upload' && !firmwareFileUrl) ||
                      (firmwareSourceType === 'url' && (!manualFirmwareUrl || !/^https?:\/\//.test(manualFirmwareUrl)))
                    }
                    className="text-sm"
                  >
                    {t('executeFirmwareUpdate', 'Update Firmware')}
                  </Button>
                </Tooltip>
              </Form.Item>
              <div className="mt-4 ">
                <div className="flex justify-between items-center">
                <div className="mb-0 font-semibold text-sm">
                  {t('firmwareHistory', 'Firmware Update History')}
                </div>
                <div>
                <Tooltip title={t('refresh')} placement="top">
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => refetchFirmwareHistory()}
                    loading={isFirmwareHistoryFetching}
                    htmlType="button"
                    type="text"
                    style={{ marginLeft: 8 }}
                    className="text-sm"
                  ></Button>
                </Tooltip>
                </div>
                </div>
                {isFirmwareHistoryLoading ? (
                  <Spin />
                ) : !firmwareList.length ? (
                  <Empty description={<span className="text-sm">{t('noFirmwareHistory', 'No firmware updates found')}</span>} />
                ) : (
                  <ul className="space-y-4 divide-y divide-gray-200 mt-2">
                    {firmwareList.map((fw) => (
                      <li key={fw._id} className="rounded-xl border border-gray-200 p-2 flex flex-col gap-2 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400 font-semibold text-sm">
                            {fw.fileName || t('firmwareFile', 'Firmware File')}
                          </span>
                          {fw.firmwareFilePath && (
                            <Button
                              type="link"
                              className="!px-0 ml-2 text-sm"
                              icon={<FileTextOutlined className="text-base" />}
                              onClick={() => {
                                // Open firmwareFilePath in new tab for download
                                window.open(fw.firmwareFilePath, '_blank');
                              }}
                            >
                              {t('download', 'Download')}
                            </Button>
                          )}
                        </div>
                        {fw.description && (
                          <div className="text-xs mt-1">
                            <b>{t('description', 'Description')}:</b> {fw.description}
                          </div>
                        )}
                        <div>
                          <Tag
                            variant="outlined"
                            color={fw.status === 'Uploaded' ? 'green' : fw.status === 'Idle' ? 'orange' : 'blue'}
                            className="ml-auto text-sm"
                            style={{ fontWeight: 500 }}
                          >
                            {fw.status}
                          </Tag>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-gray-600 text-xs">
                          <span className="flex items-center">
                            <ClockCircleOutlined className="mr-1" />
                            <span className="mr-1">{t('createdAt')}:</span>
                            {fw.created_at ? formatUnixToLocalDate(fw.created_at) : '-'}
                          </span>
                          <span className="flex items-center">
                            <UserOutlined className="mr-1" />
                            <span className="mr-1">{t('performedBy')}:</span> {fw.performerName || '-'}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex justify-center mt-4">
                  <Pagination
                    current={Math.floor(firmwareOffset / firmwarePageSize) + 1}
                    pageSize={firmwarePageSize}
                    total={firmwareTotal}
                    onChange={(page, pageSize) => {
                      setFirmwareOffset(page);
                      setFirmwarePageSize(pageSize);
                    }}
                    showSizeChanger={false}
                    className="text-sm"
                  />
                </div>
              </div>
            </>
          )}

          {command !== 'startDiagnostic' && command !== 'setLocalList' && command !== 'firmwareUpdate' && (
            <Form.Item>
              <Button
                type="primary"
                onClick={handleExecuteCommand}
                disabled={isButtonDisabled}
                loading={
                  isChangeAvailabilityLoading ||
                  isClearCacheLoading ||
                  isSetConfigLoading ||
                  isTriggerConfigLoading ||
                  isStartChargingLoading ||
                  isStartDiagnosticLoading ||
                  isRebooting ||
                  isGettingStatus ||
                  isUnlockingConnector ||
                  isTriggeringMessage
                }
              >
                {t('executeCommand')}
              </Button>
            </Form.Item>
          )}
        </Form>
      )}
      </Drawer>
    </>
  );
};

OcppCommandDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  charger: PropTypes.shape({
    _id: PropTypes.string,
    operatorId: PropTypes.string,
    serialNumber: PropTypes.string,
  }),
  refetchLocation: PropTypes.func.isRequired,
};

export default OcppCommandDrawer;
